import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Text } from '@chakra-ui/react';
import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startRegistration,
} from '@simplewebauthn/browser';
import { Button } from '@/shared/ui/chakra/button';
import { Modal } from '@/shared/ui/Modal';
import { webauthnApi } from '../api/webauthn';
import {
  dismissWebauthnRegistration,
  isUserCancelledWebauthnError,
  isWebauthnRegisteredOnThisDevice,
  isWebauthnRegistrationDismissed,
  markWebauthnRegistered,
} from '../model/webauthnLocalState';

interface Props {
  /** Flips true right after a successful password login — triggers the eligibility check. */
  active: boolean;
  /** Fires exactly once: after a register/dismiss decision, or immediately if not eligible. */
  onResolved: () => void;
}

/* Offered once per device after a password login (admin only — the register-options endpoint
   is AuthedAs<Admin>-gated). Registering here fully replaces the password for this device on
   future logins (see useWebAuthnLogin) — dismissing just re-asks next login until either
   registered or dismissed, same as nafta's BiometricPrompt. */
export function WebauthnRegisterPrompt({ active, onResolved }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!active) return;
    if (
      !browserSupportsWebAuthn() ||
      isWebauthnRegisteredOnThisDevice() ||
      isWebauthnRegistrationDismissed()
    ) {
      onResolved();
      return;
    }
    let cancelled = false;
    void platformAuthenticatorIsAvailable()
      .catch(() => false)
      .then((ok) => {
        if (cancelled) return;
        if (ok) {
          setOpen(true);
          return;
        }
        onResolved();
      });
    return () => {
      cancelled = true;
    };
    // Intentionally keyed on `active` alone — this should re-run once per login, not on every
    // re-render of the parent (onResolved is a fresh closure each time).
  }, [active]);

  const register = async () => {
    setBusy(true);
    setError(null);
    try {
      const options = await webauthnApi.registerOptions();
      const credential = await startRegistration({ optionsJSON: options });
      await webauthnApi.register(credential);
      markWebauthnRegistered();
      setOpen(false);
      onResolved();
    } catch (err) {
      if (isUserCancelledWebauthnError(err)) {
        setOpen(false);
        onResolved();
        return;
      }
      setError(t('webauthn.register_error'));
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    dismissWebauthnRegistration();
    setOpen(false);
    onResolved();
  };

  return (
    <Modal open={open} onClose={dismiss} title={t('webauthn.register_prompt_title')}>
      <Stack gap="4">
        <Text color="fg.muted">{t('webauthn.register_prompt_body')}</Text>
        {error ? (
          <Box role="alert" color="red.fg" fontSize="sm">
            {error}
          </Box>
        ) : null}
        <Stack direction="row" justify="flex-end" gap="3">
          <Button variant="ghost" onClick={dismiss} disabled={busy}>
            {t('webauthn.register_dismiss')}
          </Button>
          <Button onClick={register} loading={busy}>
            {t('webauthn.register_confirm')}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}
